import UnleashService from '@/services/unleash'
import sentry from '@/services/sentry'
import store from '@/vuex/store'
import UnleashFlagTurnOnTime from '@/constant/UnleashFlagTurnOnTime'
import PresentationApi from '@/api/presentation'
import { calculateBillingCycleNumber } from '@/utils/billing.utils'
import { coalesce } from '@/utils/promise'
import { ROUTE_NAMES, PUBLIC_ROUTES } from '@/constant/routes'
import { flags } from '@/utils/abtest.utils'

import UserApi from '@/api/user'

const { unleashClient } = UnleashService

const { canUseNewUnleashMechanism, canUseCacheProfileOnLoad } = flags

const GENERATE_OPTIONS_UNLEASH_FLAGS = {
  pickAnswer: 'release-generate-options-pick-answer',
  pickImage: 'release-generate-options-pick-answer',
  typeAnswer: 'release-generate-options-short-answer',
  matchPairs: 'release-generate-answers-match-pairs',
  correctOrder: 'release-generate-options-correct-order',
  categorise: 'release-generate-answers-categorise',
  poll: 'release-generate-options-poll',
}

const _state = {
  features: {}, // Store feature toggles
  isInitialized: false, // Track whether the client has initialized
  loading: {
    initialization: false,
    contextUpdate: false,
  },
  contextUpdateCounter: 0, // Counter for contextUpdate operations
  context: {},
  // anonymous is without any auth
  // routes: Sharing View, Login View, Register View
  hasLoadedAnonymousFlag: false,
  // other route that requires auth
  hasLoadedAuthFlag: false,
  // featureFlagInitCoalesced is used to prevent multiple feature flag init
  featureFlagInitCoalesced: null,
}

let isStartedUnleash = false

const _updateAuthFlags = (commit, context) => {
  // We have 2 flag,
  // 1. anonymous
  // 2. auth
  if (!context?.isAuth) {
    commit('setAnonymousFlag', true)
  } else {
    commit('setAuthFlag', true)
  }
}

const mutations = {
  setFeatures(state) {
    const features = unleashClient.getAllToggles()
    const featureMap = features.reduce((map, feature) => {
      map[feature.name] = feature
      return map
    }, {})
    state.features = { ...featureMap }
  },
  setInitialized(state, initialized) {
    state.isInitialized = initialized
  },
  setLoading(state, { type, loading }) {
    if (type === 'contextUpdate') {
      if (loading) {
        state.contextUpdateCounter += 1
      } else {
        state.contextUpdateCounter = Math.max(0, state.contextUpdateCounter - 1)
      }

      state.loading.contextUpdate = state.contextUpdateCounter > 0
    } else {
      state.loading[type] = loading
    }
  },
  setContext(state, context) {
    state.context = {
      ...state.context,
      ...context,
    }
  },
  startUnleash() {
    if (canUseNewUnleashMechanism) {
      UnleashService.start()
      return
    }
    // Should be removed after the new mechanism is confirmed as stable
    if (isStartedUnleash) return
    isStartedUnleash = true
    unleashClient.start()
  },
  setAnonymousFlag(state, flag) {
    state.hasLoadedAnonymousFlag = flag
  },
  setAuthFlag(state, flag) {
    state.hasLoadedAuthFlag = flag
  },
  setFeatureFlagInitCoalesced(state, payload) {
    state.featureFlagInitCoalesced = payload
  },
}

const actions = {
  async initializeFeatureToggles({ commit }) {
    commit('setLoading', { type: 'initialization', loading: true })

    try {
      unleashClient.on('initialized', async () => {
        const features = unleashClient.getAllToggles()
        if (!features || !features.length) {
          await unleashClient.updateContext({
            presenterLanguage: store.state.presenterLocale || 'en',
          })
          commit('startUnleash')
        }
        commit('setInitialized', true)
        commit('setLoading', { type: 'initialization', loading: false })
      })
      unleashClient.on('ready', async () => {
        commit('setFeatures')
        commit('setLoading', { type: 'initialization', loading: false })
        if (canUseCacheProfileOnLoad) {
          const context = unleashClient.getContext()
          _updateAuthFlags(commit, { ...context, isAuth: !!context.email })
        }
      })

      // on update context
      // More detail: https://docs.getunleash.io/reference/sdks/javascript-browser#available-events
      unleashClient.on('update', () => {
        commit('setFeatures')
        // log context
        // console.log('context :', JSON.stringify(state.context))
        // We have 2 flag,
        // 1. anonymous
        // 2. auth
        // CAUTION: probably race condition (context changes during inflight process)
        // Why removed? Because I realize this piece of code won't be called if the unleash detect nothing change from api
        // For short
        // 200 -> those code below run
        // 304 -> the on.('update') hook won't be triggered and those code below won't be run
        // if (!state?.context?.isAuth) {
        //   commit('setAnonymousFlag', true)
        // } else {
        //   commit('setAuthFlag', true)
        // }
      })
      unleashClient.on('error', (error) => {
        sentry.captureException(error, {
          tags: {
            source: 'featureToggles.module.js',
          },
        })
      })
      unleashClient.on('recovered', () => {
        // this event is only fired if after an error
        // the unleashClient successfully fetch the feature flags from the server
        // so we need to update the feature flags
        // refer to https://github.com/Unleash/unleash-js-sdk/blob/1746b0aa66c31603a3ecc52f0c7f13adb40a03dc/src/index.ts#L93
        commit('setFeatures')
      })
    } catch (error) {
      commit('setLoading', false)
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'initialization', loading: false })
    }
  },
  async updateFeatureContext({ state, commit }, context) {
    commit('setLoading', { type: 'contextUpdate', loading: true })
    commit('setContext', context)
    const contextToUse = { ...state.context }

    try {
      await unleashClient.updateContext(contextToUse)
    } catch (error) {
      // 'Failed to fetch' TypeErrors are expected when the Unleash client internally
      // aborts an in-flight request because a new updateContext() call was made before
      // the previous one completed (e.g., rapid context change from anonymous to
      // authenticated on the login page). These are not actionable and should not be
      // reported to Sentry
      // sentry.captureException(error)
    } finally {
      _updateAuthFlags(commit, contextToUse)
      commit('setLoading', { type: 'contextUpdate', loading: false })
      if (contextToUse.isAuth) {
        store.dispatch('featureSettings/fetchFeatureSettings')
      }
    }

    if (!canUseNewUnleashMechanism) {
      // redundant, already handle in the unleashClient.on('update', () => {
      commit('setFeatures')
    }
  },

  /**
    * Initialize user feature context with all necessary fields for experiments
    * @param {Object} context - Vuex action context
    * @param {Object} params - Parameters containing currentUser and presenterLanguage
    * @returns {Promise} - Promise resolving to context update result
    */
  async initializeUserFeatureContext1({
    dispatch, rootState,
  }, { currentUser, presenterLanguage }) {
    try {
      const { plan } = rootState

      const context = {
        // Base context fields (always included)
        userId: currentUser.id,
        email: currentUser.email,
        plan: currentUser.plan,
        presenterLanguage,

        // Context fields for experiment-AHA-30200-monthly-to-yearly
        billingCycle: plan?.billingCycle || null,
        billingCycleNumber: currentUser.myPlan?.subscription?.billing_cycle_anchor
          ? calculateBillingCycleNumber(currentUser.myPlan.subscription.billing_cycle_anchor)
          : null,
        qualifiedEvent: currentUser.firstEventStartedAt ? 'true' : 'false',
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('initializeUserFeatureContext', context)
      }

      return dispatch('updateFeatureContext', context)
    } catch (error) {
      // Fallback to basic context if extended context fails
      sentry.captureException(error, {
        extra: {
          action: 'initializeUserFeatureContext',
          userId: currentUser?.id,
        },
      })

      const fallbackContext = {
        userId: currentUser.id,
        email: currentUser.email,
        plan: currentUser.plan,
        presenterLanguage,
      }

      return dispatch('updateFeatureContext', fallbackContext)
    }
  },

  // Use this one to await the user flag update
  async initializeUserFeatureContext({
    dispatch,
  }, { currentUser, presenterLanguage }) {
    if (canUseNewUnleashMechanism) {
      return dispatch('initializeUserFeatureContext2', { currentUser, presenterLanguage })
    }
    return dispatch('initializeUserFeatureContext1', { currentUser, presenterLanguage })
  },
  // My 50 cents: awaiting context update is unstable IMO,
  // I am not sure the flag is ready and update to the state at that time
  // The ideal phase to confirm it to be ready is inside unleashClient.on('update', () => {
  // or the the setFeatures mutation to be more exact
  // We can set 1 more Unleash context value like:
  //  isAuth: true, and check it at the set time
  // But it's already worked and use by our colleagues, so let see if it works
  async initializeUserFeatureContext2({
    dispatch, rootState, commit, state,
  }, { currentUser, presenterLanguage }) {
    async function _doInitializeUserFeatureContext() {
      try {
        const { plan } = rootState

        if (!currentUser) {
          currentUser = rootState.currentUser
        }
        if (!currentUser) {
          currentUser = await UserApi.getUser()
        }

        const context = {
          // Base context fields (always included)
          userId: currentUser.id,
          email: currentUser.email,
          plan: currentUser.plan,
          presenterLanguage,
          isAuth: true,
          // reset the presentationCreatorEmail context
          presentationCreatorEmail: null,

          // Context fields for experiment-AHA-30200-monthly-to-yearly
          billingCycle: plan?.billingCycle || null,
          billingCycleNumber: currentUser.myPlan?.subscription?.billing_cycle_anchor
            ? calculateBillingCycleNumber(currentUser.myPlan.subscription.billing_cycle_anchor)
            : null,
          qualifiedEvent: currentUser.firstEventStartedAt ? 'true' : 'false',
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('initializeUserFeatureContext', context)
        }

        return dispatch('updateFeatureContext', context)
      } catch (error) {
        // Fallback to basic context if extended context fails
        sentry.captureException(error, {
          extra: {
            action: 'initializeUserFeatureContext',
            userId: currentUser?.id,
          },
        })

        const fallbackContext = {
          userId: currentUser.id,
          email: currentUser.email,
          plan: currentUser.plan,
          presenterLanguage,
          isAuth: true,
          // reset the presentationCreatorEmail context
          presentationCreatorEmail: null,
        }

        return dispatch('updateFeatureContext', fallbackContext)
      }
    }

    // This is to avoid multiple feature flag init
    // And we can invalidate it later if we need to
    if (!state.featureFlagInitCoalesced) {
      commit('setAuthFlag', false)
      commit('setFeatureFlagInitCoalesced', coalesce(() => _doInitializeUserFeatureContext()))
    }
    return state.featureFlagInitCoalesced.run()
  },
  async startUnleash({ commit }) {
    commit('startUnleash')
  },

  // Because sharing view is not auth required but we still need the flag
  async handlePresentationLoadFlagUnleashByShareCode({ dispatch }, shareCode) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfoByShareCode(shareCode)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
      return respFlagInfo.body
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      return error
      // do nothing
    }
  },
  async handlePresentationLoadFlagUnleash({ dispatch }, presentationId) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfo(presentationId)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      // do nothing
    }
  },
}

export const comparePresentationCreatedDateWithFlag = (presentation, flag) => {
  if (!presentation || !flag || !UnleashFlagTurnOnTime[flag]) return false
  return new Date(presentation.createdAt) > UnleashFlagTurnOnTime[flag]
}

const _getters = {
  isFeatureEnabled: (state) => (featureName) => {
    return state.features[featureName] && state.features[featureName].enabled
  },
  featureFlags: (state) => () => {
    return state.features
  },
  hasLoadedAnonymousFlag: (state) => state.hasLoadedAnonymousFlag,
  hasLoadedAuthFlag: (state) => state.hasLoadedAuthFlag,
  getVariant: (state) => (featureName) => {
    return state.features[featureName] ? state.features[featureName].variant : {
      name: 'disabled',
      enabled: false,
      payload: null,
    }
  },
  isUnleashInitialized: (state) => {
    return state.isInitialized
  },
  isLoadingFeatures: (state) => {
    return state.loading.initialization || state.loading.contextUpdate
  },
  canUseXmasDecoration: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-xmas-decoration')
  },
  canUseNewEnterpriseInquiriesForm: (_, getters) => {
    return getters.isFeatureEnabled('release-enterprise-inquiries-form')
  },
  canUseCopySharedSlides: (_, getters) => {
    return getters.isFeatureEnabled('release-copy-shared-slides')
  },
  canUseTrashBin: (_, getters) => {
    return getters.isFeatureEnabled('release-trash')
  },
  canUseCorrectCropForImageReplace: (_, getters) => {
    return getters.isFeatureEnabled('release-correct-crop-for-image-replace')
  },
  canUseAutoPrefillImageSearchTabs: (_, getters) => {
    return getters.isFeatureEnabled('release-AI-auto-prefill-image-search-keyword')
  },
  canUseAIImprovingSlideContent: (_, getters) => {
    return getters.isFeatureEnabled('release-AI-improving-slide-content')
  },
  canUseNewAutocomplete: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28900-autocomplete')
  },
  canUseTemplateLibraryInEditor: (_, getters) => {
    return getters.isFeatureEnabled('release-template-library-in-editor')
  },
  canUseRefactorRightNav: (_, getters) => {
    return getters.isFeatureEnabled('release-refactor-right-nav')
  },
  canUseGoogleDriveV2: (_, getters) => {
    return getters.isFeatureEnabled('release-google-drive-v2')
  },
  canUpgradeButtonGoToStripe: (_, getters) => {
    return getters.isFeatureEnabled('AHA-37476-enable-upgrade-button-link-stripe')
  },
  canUseRequestAccessPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28682-request-access-presentation')
  },
  canUseGenerateOptions: (_, getters, __, rootGetters) => {
    const { currentSlideType } = rootGetters
    const slideFlag = GENERATE_OPTIONS_UNLEASH_FLAGS[currentSlideType]
    return slideFlag ? getters.isFeatureEnabled(slideFlag) : false
  },
  canUseGenerateOptionsCorrectOrder: (_, getters) => {
    return getters.isFeatureEnabled('release-generate-options-correct-order')
  },

  canUseSpellchecker: (_, getters) => {
    return getters.isFeatureEnabled('release-ai-spellchecker')
  },
  canUseQuestionSuggestions: (_, getters) => {
    return getters.isFeatureEnabled('release-question-suggestions')
  },
  canUseKeywordSuggestions: (_, getters) => {
    return getters.isFeatureEnabled('release-keywords-suggestion')
  },

  isPresentationCreatedAfterTurnOnFlag: (_, __, rootState) => {
    return (flag) => {
      if (!rootState.presentation) return false
      return comparePresentationCreatedDateWithFlag(rootState.presentation, flag)
    }
  },

  canUseGoogleSlideAddonSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29230-google-slide-add-on-sync')
  },
  canUseNewComunityTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-organising-community-templates')
  },
  canUseOnboardingRemoveSpinnerWheel: (_, getters) => {
    return getters.isFeatureEnabled('release-onboarding-remove-spinnerwheel')
  },
  canUseShareableTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-shareable-template')
  },
  canUseTemplateLibraryEditorSearch: (_, getters) => {
    return getters.isFeatureEnabled('release-template-library-editor-search')
  },
  canUseReleaseNewDiscoverTemplates: (_, getters) => {
    return getters.isFeatureEnabled('release-new-discover-templates')
  },
  canUseExperimentCSATIntoTemplate: (_, getters) => {
    return getters.isFeatureEnabled('experiment-csat-into-template')
  },
  canUseThirdPartyIntegrationsInShareModal: (_, getters) => {
    return getters.isFeatureEnabled('release-promotion-3party-33784')
  },
  canUseExportRedshift: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift')
  },
  canUseExportRedshiftV4: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift-v4')
  },
  canUseExportRedshiftV5: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift-v5')
  },
  fullReleaseRedshiftV5: (_, getters) => {
    return getters.isFeatureEnabled('full-release-export-redshift-v5')
  },
  canRemovePowerPointButton: (_, getters) => {
    return getters.isFeatureEnabled('release-remove-powerpoint-30061')
  },
  canUseGoogleSlidesPromote: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33785')
  },
  canUseIntegrationsSection: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33786')
  },
  canUseNewColors: (_, getters) => {
    return getters.isFeatureEnabled('release-ahaslides-color-palette')
  },
  canUseMovePresentations: (_, getters) => {
    return getters.isFeatureEnabled('move-presentation')
  },
  isCacheCanvasBlocks: (_, getters) => {
    return getters.isFeatureEnabled('cache-canvas-blocks')
  },
  canUseNewSlideIcon: (_, getters) => {
    return getters.isFeatureEnabled('release-new-slide-icon')
  },
  canUseSlideFixSelectModeActionButtons: (_, getters) => {
    return getters.isFeatureEnabled('release-gridview-selectmode-actionbuttons-fix')
  },
  canUseEveryoneHasAnswered: (_, __, ___, rootGetters) => {
    return rootGetters['liveStatsV2/canUseLiveStatsForQuizV2']
  },
  canUseImprovedTemplateSearch: (_, getters) => {
    return getters.isFeatureEnabled('release-improved-template-search')
  },
  canUseTransactionalSlideCacheUpdate: (_, getters) => {
    return getters.isFeatureEnabled('transactional-slide-cache-update')
  },
  isEnabledNewIntegrations: (_, getters) => {
    return getters.isFeatureEnabled('is-enabled-new-integrations')
  },
  canUseNewPricing: (_, getters) => {
    return getters.isFeatureEnabled('release-new-pricing-presenter')
  },
  canUseSCIM: (_, getters) => {
    return getters.isFeatureEnabled('release-scim')
  },
  canResetPresentationTrackingContext: (_, getters) => {
    return getters.isFeatureEnabled('release-reset-presentation-tracking-context')
  },
  canUserOfferingMonthlyToYearly: (_, getters) => {
    return getters.isFeatureEnabled('experiment-AHA-30200-monthly-to-yearly')
  },
  canIncreaseLineHeightInContentSlide: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-36325-increase-line-height-in-content-slide')
  },
  canUseNewResizeImageApi: (_, getters) => {
    return getters.isFeatureEnabled('AHA-37355-use-lambda-resize')
  },
  canUserRemoveDelayRenderEditor: (_, getters) => {
    return getters.isFeatureEnabled('AHA-37687-remove-delay-500ms-render-editor')
  },
  canUseReportSession: (_, getters) => {
    return getters.isFeatureEnabled('release-report-session')
  },
  canUseDirectBulkPurchaseLinks: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-38492-direct-bulk-purchase-links')
  },
  canUseOpenEndedSampleResponses: (_, getters) => {
    return getters.isFeatureEnabled('release-open-ended-sample-responses')
  },
  canUsePresentationSearchWithShared: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-38736-presentation-search-with-shared')
  },
  canHideAmount: (_, getters) => {
    return getters.isFeatureEnabled('release-my-plan-hide-amount')
  },
  canUsePermanentDeletePresentation: (_, getters) => {
    return getters.isFeatureEnabled('permanent-delete-presentation')
  },
  canUseTrashApi: (_, getters) => {
    return getters.isFeatureEnabled('release-api-presentation-trash')
  },
  canShowTeamFreeBadge: (_, getters) => {
    return getters.isFeatureEnabled('release-show-team-free-badge')
  },
  canGetReportWithScore: (_, getters) => {
    return getters.isFeatureEnabled('release-get-report-with-score')
  },
  canUseButtonShareFolder: (_, getters) => {
    return getters.isFeatureEnabled('release-button-share-folder')
  },
  canUseISOStringForEventStartedAt: (_, getters) => {
    return getters.isFeatureEnabled('release-iso-string-for-event-started-at')
  },
  // https://ahaslides.atlassian.net/browse/AHA-39583
  // We change the current behavior, so I put this flag case we wanna revert
  // Current behavior: can't move a presentation to my presentation list
  // New behavior: can move a presentation to my presentation list
  canMovePresentationToMyPresentationList: (_, getters) => {
    return getters.isFeatureEnabled('release-allow-move-to-my-presentation')
  },
  canHideTeamSection: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-sidebar-team-section')
  },
  canUseReportQuestion: (_, getters) => {
    return getters.isFeatureEnabled('release-report-question')
  },
  canGetNewAccountMembers: (_, getters) => {
    return getters.isFeatureEnabled('release-new-api-get-account-member')
  },
  canRemoveMyImage: (_, getters) => {
    return getters.isFeatureEnabled('release-remove-my-image')
  },
  // This one is not a new feature, just for test the new Unleash mechanism.
  // Can remove later
  canUseTrashBinIconV2: (_, getters) => {
    return getters.isFeatureEnabled('release-trash-v2')
  },
  canUseAccountManager: (_, getters) => {
    return getters.isFeatureEnabled('release-account-manager')
  },
  canUseNewLookMyPlan: (_, getters) => {
    return getters.isFeatureEnabled('release-new-look-my-plan')
  },
  canReplyToSender: (_, getters) => {
    return getters.isFeatureEnabled('release-reply-to-sender')
  },
  canUseParticipantProfiles: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40041-participant-profiles-full-list')
  },
  canUseReportAsIframe: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40370-report-as-iframe')
  },
  canUseParticipantProfileResponses: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40332-participant-profile-responses')
  },
  canUseNewSfxSettings: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-39704-presenting-sfx')
  },
  canUseIdeaBoardSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39912-idea-board-slide-type')
  },
  canUsePinOnImageSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40699-pin-on-image-slide-type')
  },
  canUseRankingSlideType: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40791-ranking-slide-type')
  },
  canShowNoResultPopup: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40816-show-no-response-popup')
  },
  canUseLatexInQuizTitle: (_, getters) => {
    return getters.isFeatureEnabled('AHA-38427-latex-in-quiz-title')
  },
  canFixContextMenuPosition: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29934-fix-context-menu')
  },
  canUseSessionReport: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40593-session-report')
  },
  canFixPresentingMode: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40744-fix-presenting-mode')
  },
  canUsePresentationEditLink: (_, getters) => {
    return getters.isFeatureEnabled('AHA-32274-presentation-edit-link')
  },
  canUseAIConsentSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40635-release-ai-consent-settings')
  },
  canUseNewUiBackStage: (_, getters, __, rootGetters) => {
    return rootGetters.isBackstage && getters.isFeatureEnabled('AHA-41193-new-ui-backstage')
  },
  canUseAIConsentTeamTab: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40635-ai-consent-team-tab')
  },
  canUseNewTeamManagement: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-39002-team-management')
  },
  canUseAIConsentTeamSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40914-release-ai-consent-team-settings')
  },
  canUsePresentationDefaults: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41118-presentation-defaults')
  },
  canUseSamePricingDisplay: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41159-same-pricing-display')
  },
  canUseTeamSettingsPaywall: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41396-team-settings-paywall')
  },
}

export const isFeatureEnabled = (rootGetters, featureName) => {
  return rootGetters['featureToggles/isFeatureEnabled'](featureName)
}

export const getVariant = (rootGetters, featureName) => {
  return rootGetters['featureToggles/getVariant'](featureName)
}

export const isPresentationCreatedAfterTurnOnFlag = (rootGetters, flag) => {
  return rootGetters['featureToggles/isPresentationCreatedAfterTurnOnFlag'](flag)
}

export const isPresentationResetAfterTurnOnFlag = (presentation, flag) => {
  if (!presentation ||
    !presentation.resetTimeStamp ||
    !flag ||
    !UnleashFlagTurnOnTime[flag]
  ) {
    return false
  }
  // resetTimeStamp is a string, so need to convert it to number before parsing to Date object
  return new Date(Number(presentation.resetTimeStamp)) > UnleashFlagTurnOnTime[flag]
}

function _handleResetFeatureFlagInitCoalescedPlugin(_store, action) {
  if (action.type !== 'routeChangeAction') return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const isExitingEditorScreen = fromRoute === ROUTE_NAMES.PRESENTATION_DETAIL
    && toRoute !== ROUTE_NAMES.PRESENTATION_DETAIL

  const isExitingSharingView = fromRoute === ROUTE_NAMES.PRESENTATION_SHARE
    && toRoute !== ROUTE_NAMES.PRESENTATION_SHARE

  // Applied routes
  const isExistingAppliedRoute = isExitingEditorScreen || isExitingSharingView

  // Check if the presentation is shared or owned
  const isOwnedPresentation = _store?.state?.presentation?.userId === _store?.state?.currentUser?.id

  if (isExistingAppliedRoute && !isOwnedPresentation) {
    // Reset the User Flag using current user context after existing the Shared Route
    _store.commit('featureToggles/setFeatureFlagInitCoalesced', null)
    _store.dispatch('featureToggles/initializeUserFeatureContext', {
      currentUser: _store?.state?.currentUser,
      presenterLanguage: _store?.state?.presenterLocale,
    })
  }
}

function _handleGetCurrentUserPlugin(_store, action) {
  if (action.type !== 'routeChangeAction') return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const fromPublicRoute = PUBLIC_ROUTES.includes(fromRoute)
  const toAuthGuardRoute = !PUBLIC_ROUTES.includes(toRoute)

  if (fromPublicRoute && toAuthGuardRoute) {
    _store.dispatch('getCurrentUserAction')
  }
}

const resetFeatureFlagInitCoalescedPlugin = (_store) => {
  if (!canUseNewUnleashMechanism) return
  _store.subscribeAction((action) => {
    // Handle the Reset Feature Flag after existing the Shared Route
    _handleResetFeatureFlagInitCoalescedPlugin(_store, action)
  })
}

const getCurrentUserPlugin = (_store) => {
  if (!canUseNewUnleashMechanism) return
  _store.subscribeAction((action) => {
    // Because getCurrentUserAction is not trigger inside login or signUp component but via child component
    // So there are nothing registered (mixpanel, sentry, ...)
    // We need to manually trigger the getCurrentUserAction
    _handleGetCurrentUserPlugin(_store, action)
  })
}

const plugins = [
  resetFeatureFlagInitCoalescedPlugin,
  getCurrentUserPlugin,
]

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
  plugins,
}
